/**
 * Onboarding Progress Widget — Dashboard.
 *
 * Shows the setup checklist and a % progress bar while the tenant is still
 * in "insufficient_evidence" state (mirrors the AI Executive Brief). Auto-
 * hides as soon as the readiness engine has enough evidence to score.
 *
 * Data contract: GET /api/dashboard/readiness
 *   → { readiness_status, setup_checklist: [{key, label, done}], populated_axes, ... }
 *
 * "Continue Setup" CTA jumps to the first uncompleted step's home route.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, CheckCircle2, Clock, Sparkles, X, ChevronDown, ChevronUp,
} from "lucide-react";

// Map each checklist key to the app route it lives on. Anything unmapped
// falls back to the home dashboard.
const STEP_ROUTE = {
  organisation:  "/sites",
  staff:         "/users",
  workforce:     "/users",
  training:      "/training",
  service_users: "/clients",
  care_plans:    "/care-plans",
  risk:          "/risk-register",
  medication:    "/mar-chart",
  policies:      "/policies",
  audits:        "/audits",
  supervisions:  "/supervisions",
  incidents:     "/incidents",
  complaints:    "/complaints",
};

const DISMISS_KEY = "ayobell_onboarding_widget_dismissed";

export default function OnboardingWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/readiness");
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-hide when readiness is assessed (evidence sufficient) or widget dismissed.
  if (loading || dismissed || !data) return null;
  if (data.readiness_status !== "insufficient_evidence") return null;

  const checklist = data.setup_checklist || [];
  if (checklist.length === 0) return null;

  const doneCount = checklist.filter((c) => c.done).length;
  const total = checklist.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const firstOutstanding = checklist.find((c) => !c.done);

  const continueSetup = () => {
    if (firstOutstanding) {
      const to = STEP_ROUTE[firstOutstanding.key] || "/";
      navigate(to);
    }
  };
  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="onboarding-widget"
      className="relative bg-gradient-to-r from-sky-50 via-white to-emerald-50 dark:from-sky-950/40 dark:via-slate-900 dark:to-emerald-950/40 border border-sky-200 dark:border-sky-800 rounded-lg p-4 md:p-5 shadow-sm"
    >
      <button
        onClick={dismiss}
        data-testid="onboarding-widget-dismiss"
        className="absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-white/70"
        title="Dismiss for this session"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start justify-between gap-4 pr-6 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-sky-700 dark:text-sky-300 font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Getting started
          </div>
          <h3 className="font-heading text-base font-semibold text-slate-900 dark:text-slate-100 mt-0.5" data-testid="onboarding-widget-title">
            Complete your CQC readiness baseline
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 max-w-xl">
            {doneCount} of {total} steps done · {pct}% complete. AYOBELL will
            start scoring your inspection readiness once the first three areas
            hold real data.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {firstOutstanding && (
            <Button
              size="sm"
              onClick={continueSetup}
              data-testid="btn-onboarding-continue"
              className="h-9 bg-sky-700 hover:bg-sky-800"
            >
              Continue Setup <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed((c) => !c)}
            data-testid="btn-onboarding-toggle"
            className="h-9"
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden" data-testid="onboarding-progress-bar">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Checklist */}
      {!collapsed && (
        <ul
          className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5"
          data-testid="onboarding-checklist"
        >
          {checklist.map((c) => {
            const to = STEP_ROUTE[c.key];
            const row = (
              <span className="flex items-start gap-2 text-sm">
                {c.done ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                ) : (
                  <Clock className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                )}
                <span className={c.done ? "text-slate-500 line-through" : "text-slate-800 dark:text-slate-200 hover:text-sky-700"}>
                  {c.label}
                </span>
              </span>
            );
            return (
              <li key={c.key} data-testid={`onboarding-step-${c.key}`}>
                {!c.done && to ? (
                  <button type="button" onClick={() => navigate(to)} className="text-left w-full">
                    {row}
                  </button>
                ) : row}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
